import {
  YStack,
  View,
  AnimatePresence,
  Anchor,
  isWeb,
  Image,
  Text,
  Paragraph,
  XStack,
} from 'tamagui'
import HerosparkLogoSvg from '@assets/hero/herospark-logo.svg'
import HerosparkCircleLogoSvg from '@assets/hero/herospark-circle-logo.svg'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { SchoolSelect } from '@components/SchoolSelect'
import { useState, useMemo, useEffect, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetSchoolsByEmail } from '@services/api/graphql/student'
import { useToastController } from '@tamagui/toast'
import { useSignIn } from '@hooks/useUser'
import { router, useLocalSearchParams } from 'expo-router'
import { useSession } from '@hooks/useSession'
import { AUTH_TOKEN, NATIVE_BASE_URL } from '@constants/storages'
import { useSchoolThemeStore } from '@stores/useSchoolThemeStore'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import {
  KeyboardAvoidingView,
  Platform,
  VirtualizedList,
  Keyboard,
  ActivityIndicator,
} from 'react-native'
import { useThemeParams, useHasFeature } from '@hooks/useThemeParams'
import { DISABLE_HEROSPARK_LOGO } from '@constants/features'
import { LoadingScreen } from '@components/LoadingScreen'
import { ISchool } from '@dtos/Theme'
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope'
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey'

type Step = 'email' | 'school' | 'password'

type School = {
  id: string
  name: string
  organizationId: number
  url: string
  logo?: string
}

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// No web os campos precisam viver dentro de um <form> — sem isso o Chrome
// avisa "Password field is not contained in a form" e os gerenciadores de
// senha não reconhecem o par email/senha. display:contents mantém o layout
// flex intacto; o submit é tratado pelos handlers da tela.
// O submit em si continua nos handlers da tela (onPress do botão e
// onSubmitEditing dos campos); aqui só evitamos o reload padrão do form.
function LoginFormWrapper({ children }: { children: ReactNode }) {
  if (!isWeb) return <>{children}</>

  return (
    <form
      style={{ display: 'contents' }}
      onSubmit={(event) => event.preventDefault()}
    >
      {children}
    </form>
  )
}

export default function Login() {
  const { t } = useTranslation('Login')
  // Toque numa notificação de outra escola: chega com a escola dela.
  const { schoolId: preselectedSchoolId } = useLocalSearchParams<{
    schoolId?: string
  }>()
  const { signInByToken, user: sessionUser } = useSession()
  const [email, setEmail] = useState(
    preselectedSchoolId && sessionUser?.email ? sessionUser.email : '',
  )
  const [password, setPassword] = useState('')
  const [selectedSchool, setSelectedSchool] = useState<School | undefined>()
  const [currentStep, setCurrentStep] = useState<Step>('email')
  const [schools, setSchools] = useState<School[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [themeSchool, setThemeSchool] = useState<ISchool | null>(null)
  const [hasEmailError, setHasEmailError] = useState(false)
  const [hasPasswordError, setHasPasswordError] = useState(false)
  const [isKeyboardVisible, setKeyboardVisible] = useState(false)
  const toast = useToastController()
  const { signIn, loading: isSignInLoading } = useSignIn()
  const { getSchoolsByEmail } = useGetSchoolsByEmail()
  const { setItem: setStorageSchoolUrl } = useAsyncStorage(NATIVE_BASE_URL)
  const {
    setItem: setStorageToken,
    getItem: getStorageToken,
    removeItem: removeStorageToken,
  } = useAsyncStorage(AUTH_TOKEN)
  // Enquanto verifica a sessão o formulário não aparece (evita piscar o login
  // para quem já está autenticado).
  const [isCheckingSession, setIsCheckingSession] = useState(isWeb)
  const {
    data: themeParams,
    loading: themeParamsLoading,
    refreshParams,
  } = useThemeParams()
  // escola white label (deactivate_copyright) não exibe o selo da HeroSpark,
  // como já acontece no rodapé da área de membros
  const hideHerosparkLogo = useHasFeature(DISABLE_HEROSPARK_LOGO)

  const setLoginThemeSchool = useSchoolThemeStore(
    (state) => state.setLoginSchool,
  )

  // No app, sem sessão, o tema da escola só entra quando ela é escolhida aqui.
  useEffect(() => {
    if (isWeb) return
    setLoginThemeSchool(currentStep === 'email' ? null : selectedSchool?.id)
  }, [currentStep, selectedSchool, setLoginThemeSchool])

  useEffect(() => {
    if (isWeb) return
    return () => setLoginThemeSchool(null)
  }, [setLoginThemeSchool])

  const hasThemeSchool = useMemo(() => {
    return Boolean(themeSchool)
  }, [themeSchool])

  const displaySchoolSelect = useMemo(() => {
    return currentStep !== 'email' && !hasThemeSchool
  }, [currentStep, hasThemeSchool])

  const isButtonDisabled = useMemo(() => {
    if (hasThemeSchool) {
      return !password.trim() || !email.trim() || !isValidEmail(email)
    }

    switch (currentStep) {
      case 'email':
        return !email.trim() || !isValidEmail(email)
      case 'school':
        return !selectedSchool
      case 'password':
        return !password.trim()
    }
  }, [currentStep, email, selectedSchool, password, hasThemeSchool])

  const isCurrentStepPassword = useMemo(() => {
    return currentStep === 'password'
  }, [currentStep])

  const isCurrentStepSchool = useMemo(() => {
    return currentStep === 'school'
  }, [currentStep])

  const hasOnlyOneSchool = useMemo(() => {
    return schools.length === 1
  }, [schools])

  const animationProps = {
    opacity: 0,
    y: -10,
  }

  const themeSchoolContainerStyles = useMemo(
    () => ({
      maxWidth: hasThemeSchool ? 390 : 640,
      padding: hasThemeSchool ? ('$size.spacing-md' as const) : 0,
      borderRadius: hasThemeSchool ? ('$border-radius-md' as const) : 0,
      backgroundColor: hasThemeSchool
        ? ('$bg-surface' as const)
        : 'transparent',
      gap: hasThemeSchool
        ? ('$size.spacing-md' as const)
        : isKeyboardVisible && !isWeb
          ? 40
          : 141,
      $gtSm: {
        padding: hasThemeSchool ? ('$size.spacing-lg' as const) : 0,
      },
    }),
    [hasThemeSchool, isKeyboardVisible],
  )

  const themeSchoolContentStyles = useMemo(
    () => ({
      gap: hasThemeSchool
        ? ('$size.spacing-md' as const)
        : ('$size.spacing-xl' as const),
    }),
    [hasThemeSchool],
  )

  const themeSchoolAnchorStyles = useMemo(
    () => ({
      color: hasThemeSchool ? ('$accent' as const) : ('$text-primary' as const),
      fontWeight: hasThemeSchool ? ('700' as const) : ('400' as const),
      fontFamily: hasThemeSchool
        ? ('$mulishBold' as const)
        : ('$mulishRegular' as const),
      fontSize: hasThemeSchool
        ? ('$font-size-xxxs' as const)
        : ('$font-size-xxs' as const),
      width: '100%',
    }),
    [hasThemeSchool],
  )

  const handleEmailChange = (text: string) => {
    setHasEmailError(false)

    if (currentStep !== 'email') {
      setCurrentStep('email')
      setSchools([])
      setSelectedSchool(undefined)
      setHasPasswordError(false)
      setPassword('')
    }

    setEmail(text)
  }

  const handlePasswordChange = (text: string) => {
    setPassword(text)
    setHasPasswordError(false)
  }

  const handleClearEmail = () => {
    setEmail('')
    setHasEmailError(false)
  }

  const fetchSchoolsByEmail = async (email: string, page = 1) => {
    if (!email.trim()) return null

    const isFirstPage = page === 1
    if (isFirstPage) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }

    try {
      const { data } = await getSchoolsByEmail({
        variables: {
          email,
          page,
          perPage: 10,
        },
      })

      const {
        schools: newSchools,
        totalPages: total,
        currentPage: pageNumber,
      } = data.studentSchools

      if (newSchools.length === 0 && isFirstPage) {
        return null
      }

      setTotalPages(total)
      setCurrentPage(pageNumber)

      return newSchools
    } catch (error) {
      toastErrorMessage(
        'Erro inesperado',
        'Ocorreu um erro ao buscar as escolas. Tente novamente.',
      )
      return null
    } finally {
      if (isFirstPage) {
        setIsLoading(false)
      } else {
        setIsLoadingMore(false)
      }
    }
  }

  const toastErrorMessage = (titulo: string, mensagem: string) => {
    toast.show(titulo, {
      message: mensagem,
      type: 'error',
    })
  }

  const handleLoadMore = async () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1
      const newSchools = await fetchSchoolsByEmail(email, nextPage)

      if (newSchools) {
        setSchools((prevSchools) => [...prevSchools, ...newSchools])
      }
    }
  }

  const handleContinue = async () => {
    if (hasThemeSchool) {
      handleLogin()
      return
    }

    switch (currentStep) {
      case 'email': {
        const schools = await fetchSchoolsByEmail(email)
        if (schools) {
          const preselectedSchool = schools.find(
            (school: School) => `${school.id}` === preselectedSchoolId,
          )
          setSchools(schools)
          setHasEmailError(false)
          if (preselectedSchool) {
            setSelectedSchool(preselectedSchool)
            setCurrentStep('password')
          } else {
            setCurrentStep('school')
          }
        } else {
          setHasEmailError(true)
        }
        break
      }
      case 'school':
        setCurrentStep('password')
        break
      case 'password':
        handleLogin()
        break
    }
  }

  const handleSchoolChange = (school: School) => {
    setSelectedSchool(school)
    setCurrentStep('password')
    setPassword('')
  }

  const handleLogin = async () => {
    if (!selectedSchool && !hasThemeSchool) return

    try {
      const selectedSchoolId = hasThemeSchool
        ? themeSchool?.id
        : selectedSchool?.id
      const organizationId = hasThemeSchool
        ? themeSchool?.organizationId
        : selectedSchool?.organizationId

      if (!selectedSchoolId || !organizationId) {
        toastErrorMessage('Erro ao fazer login', 'Escola não encontrada.')
        return
      }

      const { data } = await signIn({
        variables: {
          auth: {
            email,
            password,
            schoolId: selectedSchoolId.toString(),
            organizationId,
          },
        },
      })

      const {
        status,
        token: tokenFromResponse,
        redirectUrl,
      } = data?.userSignIn ?? {
        status: 'error',
        error: 'Erro inesperado',
      }

      if (status === 'success' && tokenFromResponse) {
        await setStorageToken(tokenFromResponse)
        const schoolUrl = hasThemeSchool
          ? window.location.origin
          : selectedSchool?.url
        if (schoolUrl) await setStorageSchoolUrl(schoolUrl)

        const signInSuccess = await signInByToken(tokenFromResponse)

        await refreshParams()

        if (signInSuccess) {
          setHasPasswordError(false)
          // No web o magic link cria a sessão cookie do Devise no core antes
          // de voltar para a home — sem ela as páginas hero_theme deslogam.
          isWeb
            ? (window.location.href = redirectUrl ?? '/')
            : router.replace('/')
        } else {
          setHasPasswordError(true)
          toastErrorMessage(
            'Erro ao fazer login',
            'Não foi possível autenticar o usuário.',
          )
        }
      } else {
        setHasPasswordError(true)
      }
    } catch (error) {
      setHasPasswordError(true)
      toastErrorMessage(
        'Erro ao fazer login',
        'Ocorreu um erro ao fazer login. Tente novamente.',
      )
    }
  }

  const renderButtonText = useMemo(() => {
    if (isSignInLoading)
      return <ActivityIndicator size="small" color="$neutral-0" />
    return isCurrentStepPassword || hasThemeSchool
      ? t('button.login')
      : t('button.continue')
  }, [isSignInLoading, isCurrentStepPassword, hasThemeSchool, t])

  useEffect(() => {
    if (hasOnlyOneSchool && isCurrentStepSchool) {
      setSelectedSchool(schools[0])
      setCurrentStep('password')
    }
  }, [schools, isCurrentStepSchool, hasOnlyOneSchool])

  useEffect(() => {
    if (!isWeb) return
    const { school } = themeParams ?? {}

    if (school) {
      setThemeSchool(school)
    }
  }, [themeParams, themeParamsLoading])

  // /users/sign_in é rota guest: o SessionProvider não autentica nem
  // redireciona nela, então quem já tem sessão veria o formulário de novo.
  // A verificação usa só o token guardado — não dispara /auth/upgrade, que
  // devolveria 401 no console para todo visitante deslogado (o caso comum
  // aqui). Quem está logado apenas pelo cookie do hero_theme continua vendo o
  // formulário.
  useEffect(() => {
    if (!isWeb) return

    let active = true

    const redirectWhenAlreadyLogged = async () => {
      try {
        const storedToken = await getStorageToken()

        if (storedToken && (await signInByToken(storedToken))) {
          // segue em loading até a navegação acontecer
          window.location.href = '/'
          return
        }

        if (storedToken) await removeStorageToken()
      } catch {
        // token ilegível/serviço fora: cai no formulário
      }

      if (active) setIsCheckingSession(false)
    }

    redirectWhenAlreadyLogged()

    return () => {
      active = false
    }
    // roda uma única vez, no mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => {
        setKeyboardVisible(true)
      },
    )
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => {
        setKeyboardVisible(false)
      },
    )

    return () => {
      keyboardDidShowListener.remove()
      keyboardDidHideListener.remove()
    }
  }, [])

  if (themeParamsLoading || isCheckingSession) {
    return <LoadingScreen />
  }

  const getItem = (_data: unknown, index: number) => ({
    id: String(index),
    content: 'login',
  })

  const renderItem = () => (
    <View
      backgroundColor="$bg-page"
      width="100%"
      minHeight="100%"
      paddingVertical={102}
      paddingHorizontal="$size.spacing-xs"
      alignItems="center"
      flex={1}
      gap="$size.spacing-xs"
    >
      <YStack width="100%" alignItems="center" {...themeSchoolContainerStyles}>
        {hasThemeSchool ? (
          <YStack width="100%" alignItems="center" gap="$size.spacing-xs">
            {themeSchool?.logo && (
              <Image
                source={{ uri: themeSchool?.logo }}
                alt={themeSchool?.name}
                width={220}
                height={71}
                resizeMode="contain"
              />
            )}
            <Paragraph
              color="$text-primary"
              fontSize="$font-size-md"
              textAlign="center"
              lineHeight={30}
            >
              <Text color="$text-primary" fontWeight={700}>
                {themeSchool?.name}
              </Text>
              {' está feliz em te receber!'}
            </Paragraph>

            <Text
              color="$text-secondary"
              fontSize="$font-size-xs"
              fontFamily="$mulishRegular"
              lineHeight={24}
            >
              Entre com o seu email e senha
            </Text>
          </YStack>
        ) : (
          <HerosparkLogoSvg height={43} width={220} />
        )}

        <YStack width="100%" {...themeSchoolContentStyles}>
          <LoginFormWrapper>
            <YStack gap="$size.spacing-xs">
              <AnimatePresence>
                <YStack
                  key="email"
                  animation="quick"
                  enterStyle={animationProps}
                  exitStyle={animationProps}
                >
                  <Input
                    icon={faEnvelope}
                    label={t('email.label')}
                    placeholder={t('email.placeholder')}
                    value={email}
                    onChangeText={handleEmailChange}
                    onClear={handleClearEmail}
                    autoComplete="email"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    onSubmitEditing={handleContinue}
                    errorMessage={hasEmailError ? t('email.error') : false}
                    displayErrorIcon={false}
                  />
                </YStack>

                {displaySchoolSelect && (
                  <YStack
                    key="school"
                    animation="quick"
                    enterStyle={animationProps}
                    exitStyle={animationProps}
                  >
                    <SchoolSelect
                      label={t('school.label')}
                      description={t('school.description')}
                      options={schools}
                      value={selectedSchool}
                      onValueChange={handleSchoolChange}
                      autoComplete="current-password"
                      onLoadMore={handleLoadMore}
                      isLoadingMore={isLoadingMore}
                      hasMoreItems={currentPage < totalPages}
                      onOpenSchoolSelect={() => Keyboard.dismiss()}
                    />
                  </YStack>
                )}

                {(isCurrentStepPassword || hasThemeSchool) && (
                  <YStack
                    key="password"
                    animation="quick"
                    enterStyle={animationProps}
                    exitStyle={animationProps}
                  >
                    <Input
                      icon={faKey}
                      label={t('password.label')}
                      placeholder={t('password.placeholder')}
                      value={password}
                      onChangeText={handlePasswordChange}
                      secureTextEntry
                      autoComplete="current-password"
                      onSubmitEditing={handleContinue}
                      errorMessage={
                        hasPasswordError ? t('password.error') : false
                      }
                      displayErrorIcon={false}
                    />
                  </YStack>
                )}
              </AnimatePresence>
            </YStack>

            <YStack gap="$size.spacing-xxxs">
              <Button
                variant="primary"
                size="large"
                onPress={handleContinue}
                disabled={isButtonDisabled || isLoading}
                animation="quick"
                pressStyle={{ scale: 0.97 }}
                loading={isSignInLoading}
              >
                {renderButtonText}
              </Button>

              {(isCurrentStepPassword || hasThemeSchool) && (
                <Anchor
                  {...themeSchoolAnchorStyles}
                  cursor="pointer"
                  href={
                    hasThemeSchool
                      ? '/users/password/new'
                      : `${selectedSchool?.url}/users/password/new`
                  }
                  // No domínio da escola a recuperação é a rota interna: mesma
                  // aba. No fluxo do app o link sai para o domínio de outra
                  // escola, onde abrir fora faz sentido.
                  target={hasThemeSchool ? '_self' : '_blank'}
                >
                  {t('password.forgot-password')}
                </Anchor>
              )}
            </YStack>
          </LoginFormWrapper>
        </YStack>
      </YStack>

      {hasThemeSchool && !hideHerosparkLogo && (
        <XStack
          alignItems="center"
          justifyContent="flex-end"
          gap="$size.spacing-xxs"
          width="100%"
          maxWidth={390}
        >
          <HerosparkCircleLogoSvg height={26} width={26} />
          <Text
            color="$text-placeholder"
            fontSize="$font-size-xs"
            fontFamily="$mulishRegular"
            lineHeight={24}
          >
            Feito por Herospark
          </Text>
        </XStack>
      )}
    </View>
  )

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flexGrow: 1 }}
    >
      <View bg="$bg-page" flex={1}>
        <VirtualizedList
          data={[{}]}
          renderItem={renderItem}
          keyExtractor={() => 'login'}
          getItem={getItem}
          getItemCount={() => 1}
          keyboardShouldPersistTaps="handled"
        />
      </View>
    </KeyboardAvoidingView>
  )
}
